import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CalendlyConnect from '../components/settings/CalendlyConnect';
import GmailConnect from '../components/settings/GmailConnect';
import InventorProfileForm from '../components/inventor/InventorProfileForm';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ROLE_LABELS = {
  inventor: 'Inventor',
  company: 'Company',
  pending_company: 'Company (Pending Approval)',
  admin: 'Admin',
};


function StudioSettings() {
  const { getAccessToken } = useAuth();
  const [studioProfile, setStudioProfile] = useState({ studio_name: '', bio: '', portfolio_url: '', logo_url: '' });
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [profileRes, membersRes] = await Promise.all([
        fetch(`${API_URL}/api/studio/profile`, { headers }),
        fetch(`${API_URL}/api/studio/members`, { headers }),
      ]);

      if (profileRes.ok) {
        const { studio_profile } = await profileRes.json();
        if (studio_profile) {
          setStudioProfile({
            studio_name: studio_profile.studio_name || '',
            bio: studio_profile.bio || '',
            portfolio_url: studio_profile.portfolio_url || '',
            logo_url: studio_profile.logo_url || '',
          });
        }
      }

      if (membersRes.ok) {
        const { members: m } = await membersRes.json();
        setMembers(m || []);
      }
    } catch (err) {
      console.error('Studio fetch error:', err);
    }
  }, [getAccessToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeMembers = members.filter(m => m.status === 'active');
  const additionalMemberCost = activeMembers.length * 49;

  const handleSaveProfile = async () => {
    setSaving(true);
    setError('');
    try {
      const token = await getAccessToken();
      const res = await fetch(`${API_URL}/api/studio/profile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(studioProfile),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setError('');
    setInviteSuccess('');
    try {
      const token = await getAccessToken();
      const res = await fetch(`${API_URL}/api/studio/invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), personal_message: inviteMessage.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send invite');
      setInviteSuccess(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      setInviteMessage('');
      fetchData();
    } catch (err) {
      setError(err.message);
    }
    setInviting(false);
  };

  const handleRemove = async (memberId) => {
    setError('');
    try {
      const token = await getAccessToken();
      const res = await fetch(`${API_URL}/api/studio/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove member');
      }
      setConfirmRemove(null);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="mt-6 space-y-6">
      {/* Studio Profile */}
      <div className="glass p-7 max-w-2xl">
        <h2 className="font-heading text-xl text-vault-navy mb-5">Studio Profile</h2>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-[#F97066] text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Studio Name</label>
            <input
              type="text"
              value={studioProfile.studio_name}
              onChange={e => setStudioProfile(prev => ({ ...prev, studio_name: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-vault-navy text-sm focus:border-vault-gold focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Bio</label>
            <textarea
              value={studioProfile.bio}
              onChange={e => setStudioProfile(prev => ({ ...prev, bio: e.target.value }))}
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-vault-navy text-sm focus:border-vault-gold focus:outline-none transition-colors resize-none"
            />
            <div className="text-[11px] text-gray-400 mt-1 text-right">{studioProfile.bio.length}/500</div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Portfolio URL</label>
            <input
              type="url"
              value={studioProfile.portfolio_url}
              onChange={e => setStudioProfile(prev => ({ ...prev, portfolio_url: e.target.value }))}
              placeholder="https://..."
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-vault-navy text-sm focus:border-vault-gold focus:outline-none transition-colors"
            />
          </div>
          <button
            onClick={handleSaveProfile}
            disabled={saving || !studioProfile.studio_name.trim()}
            className="px-6 py-3 rounded-xl bg-vault-gold text-white font-semibold text-sm disabled:opacity-50 hover:brightness-110 transition-all"
          >
            {saving ? 'Saving...' : saved ? 'Saved \u2713' : 'Save Studio Profile'}
          </button>
        </div>
      </div>

      {/* Team Members */}
      <div className="glass p-7 max-w-2xl">
        <h2 className="font-heading text-xl text-vault-navy mb-2">Studio Members</h2>
        <p className="text-sm text-gray-500 mb-5">Manage your studio team members.</p>

        {/* Member list */}
        {members.length > 0 ? (
          <div className="space-y-3 mb-6">
            {members.map(m => (
              <div key={m.id} className="flex items-center justify-between p-4 rounded-xl bg-white border border-gray-100">
                <div className="flex items-center gap-3">
                  {m.member_profile?.avatar_url ? (
                    <img src={m.member_profile.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center text-vault-navy text-sm font-semibold">
                      {(m.member_user?.display_name || m.invited_email)?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium text-vault-navy">{m.member_user?.display_name || m.invited_email}</div>
                    <div className="text-xs text-gray-400">{m.member_user?.email || m.invited_email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    m.status === 'active' ? 'bg-green-50 text-green-600' :
                    m.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {m.status === 'active' ? 'Active' : m.status === 'pending' ? 'Invite Pending' : 'Removed'}
                  </span>
                  {m.status !== 'removed' && (
                    confirmRemove === m.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRemove(m.id)}
                          className="text-xs text-red-500 font-semibold hover:text-red-700"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className="text-xs text-gray-400 font-semibold hover:text-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRemove(m.id)}
                        className="text-xs text-red-400 font-medium hover:text-red-600"
                      >
                        Remove
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 rounded-xl bg-indigo-50/50 text-center mb-6">
            <p className="text-sm text-gray-500">Your studio account includes just you right now. Invite collaborators to share your idea vault and work together on pitches.</p>
          </div>
        )}

        {/* Billing summary */}
        <div className="p-5 rounded-xl bg-white border-2 border-vault-gold/30 mb-6">
          <div className="flex justify-between text-sm text-vault-navy mb-2">
            <span>Studio base</span>
            <span className="font-medium">$149/month</span>
          </div>
          <div className="flex justify-between text-sm text-vault-navy mb-3">
            <span>Additional members: {activeMembers.length} x $49</span>
            <span className="font-medium">${additionalMemberCost}/month</span>
          </div>
          <div className="border-t border-gray-200 pt-3 flex justify-between text-sm font-semibold text-vault-navy">
            <span>Total</span>
            <span>${149 + additionalMemberCost}/month</span>
          </div>
        </div>

        {/* Invite new member */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-vault-navy">Invite New Member</h3>
          <input
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            placeholder="Member's email address"
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-vault-navy text-sm focus:border-vault-gold focus:outline-none transition-colors"
          />
          <textarea
            value={inviteMessage}
            onChange={e => setInviteMessage(e.target.value)}
            placeholder="Personal message (optional)"
            rows={2}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-vault-navy text-sm focus:border-vault-gold focus:outline-none transition-colors resize-none"
          />
          {inviteSuccess && (
            <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-green-600 text-sm">{inviteSuccess}</div>
          )}
          <button
            onClick={handleInvite}
            disabled={inviting || !inviteEmail.trim()}
            className="px-6 py-3 rounded-xl bg-vault-gold text-white font-semibold text-sm disabled:opacity-50 hover:brightness-110 transition-all"
          >
            {inviting ? 'Sending...' : 'Send Studio Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { profile } = useAuth();

  return (
    <div>
      <h1 className="font-heading text-3xl text-vault-navy mb-6">Account Settings</h1>

      <div className="glass p-7 max-w-2xl">
        <h2 className="font-heading text-xl text-vault-navy mb-5">Account</h2>

        <div className="space-y-5">
          <div>
            <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider">Name</label>
            <div className="text-vault-navy font-medium mt-1">{profile?.display_name}</div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider">Email</label>
            <div className="text-vault-navy font-medium mt-1">{profile?.email}</div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider">Role</label>
            <div className="text-vault-navy font-medium mt-1">{ROLE_LABELS[profile?.role]}</div>
          </div>
        </div>
      </div>

      {/* Inventor profile */}
      {profile?.role === 'inventor' && (
        <div className="mt-6">
          <InventorProfileForm />
        </div>
      )}

      {/* Studio settings — only for studio owners */}
      {profile?.subscription_tier === 'studio_149' && <StudioSettings />}

      {/* Integrations — Calendly only for admin */}
      {profile?.role === 'admin' && (
        <div className="mt-6">
          <CalendlyConnect />
        </div>
      )}

      {/* Gmail for admin only */}
      {profile?.role === 'admin' && (
        <div className="mt-6">
          <GmailConnect />
        </div>
      )}
    </div>
  );
}
